/**
 * Explanation Slider: accordion on mobile, tabs on desktop.
 *
 * Follows the same DOM conventions as field_group/element.horizontal_tabs:
 *  - Tab buttons are <li class="h-tab-button"> inside [data-horizontal-tabs-list]
 *  - Active tab gets class "selected" on the <li>
 *  - Hidden panes get class "h-tab-hidden" and are jQuery .hide()-n
 *
 * Mobile  (< 760px): <ul> stays visually hidden, pane headers work as accordion triggers.
 * Desktop (≥ 760px): <ul> is revealed, pane headers are hidden, content shown as tabs.
 */

import $ from 'jquery';

const MOBILE_BREAKPOINT = 960;

const isMobile = () => window.innerWidth < MOBILE_BREAKPOINT;

const getPanes = $container =>
  $container.find('[data-horizontal-tabs-panes]').children('.h-tabs-pane');

const getTabList = $container =>
  $container.children('[data-horizontal-tabs-list]');

/**
 * Activate one tab pane by index, hide the rest.
 */
const activateTab = ($container, index) => {
  getPanes($container).each((i, el) => {
    if (i === index) {
      $(el)
        .removeClass('h-tab-hidden')
        .addClass('is-open');
    } else {
      $(el)
        .addClass('h-tab-hidden')
        .removeClass('is-open');
    }
  });

  getTabList($container)
    .find('> li')
    .each((i, el) => {
      $(el).toggleClass('selected', i === index);
    });
};

/**
 * Build the <li> tab buttons from the pane headers.
 * Mirrors the title-extraction logic of field_group's horizontal-tabs.js.
 */
const buildTabList = $container => {
  const $tabList = getTabList($container);
  $tabList.empty();

  getPanes($container).each((i, el) => {
    const $pane = $(el);
    const $header = $pane.children('.h-tabs-pane__header');
    const $detailsTitle = $header.find('.details-title');

    let title;
    if ($detailsTitle.length) {
      title = $detailsTitle
        .find('> span:last-child')
        .html()
        .trim();
    } else {
      title = $header.html().trim();
    }

    const $link = $('<a>')
      .attr('href', `#${$pane.attr('id')}`)
      .append($('<strong>').html(title));

    const $item = $('<li class="h-tab-button" tabindex="-1">').append($link);

    $link.on('click', e => {
      e.preventDefault();
      activateTab($container, i);
    });

    $tabList.append($item);
  });

  $tabList.find('> li:first').addClass('first');
  $tabList.find('> li:last').addClass('last');
};

/**
 * Desktop mode: reveal the tab list and show one pane at a time as tabs.
 *
 * Pane header hiding and the equal-height sizing are handled in CSS (panes are
 * grid-stacked so the block sizes to the tallest slide). See #4206.
 */
const enableDesktopTabs = $container => {
  getTabList($container).removeClass('visually-hidden');

  activateTab($container, 0);
};

/**
 * Mobile mode: hide tab list, show pane headers as accordion triggers.
 */
const enableMobileAccordion = $container => {
  getTabList($container).addClass('visually-hidden');

  const $panes = getPanes($container);

  $panes.removeClass('h-tab-hidden');

  $panes.children('.h-tabs-pane__header').on('click.explanationSlider', e => {
    const $pane = $(e.currentTarget).closest('.h-tabs-pane');
    const wasOpen = $pane.hasClass('is-open');
    $panes.removeClass('is-open');
    if (!wasOpen) {
      $pane.addClass('is-open');
    }
  });
};

const initSlider = container => {
  const $container = $(container);

  $container.find('[data-horizontal-tabs-panes]').addClass('h-tabs-panes');

  buildTabList($container);

  let currentMode = null;

  const applyMode = () => {
    const mode = isMobile() ? 'mobile' : 'desktop';
    if (mode === currentMode) return;
    currentMode = mode;

    $container.find('.h-tabs-pane__header').off('click.explanationSlider');

    if (mode === 'mobile') {
      enableMobileAccordion($container);
    } else {
      enableDesktopTabs($container);
    }
  };

  applyMode();

  let resizeTimer;
  $(window).on('resize.explanationSlider', () => {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(applyMode, 100);
  });
};

Drupal.behaviors.explanationSlider = {
  attach(context) {
    /* eslint-disable */
    $(once('explanation-slider', '[data-horizontal-tabs]', context)).each(function() {
      initSlider(this);
    });
    /* eslint-enable */
  },
};
